<template>
  <div class="vouchers">
    <div class="voucher-generator">
      <div class="settings" v-loading="generating">
        <h5>Generate Vouchers</h5>
        <el-form label-position="left" label-width="120px" :model="form" :rules="formRules" ref="form">
          <el-form-item label="Product" prop="id">
            <el-select
              :value="form.id"
              @input="updateForm({id: $event})"
              filterable>
              <el-option-group
                v-for="group in productOptions"
                :key="group.label"
                :label="group.label">
                <el-option
                  v-for="product in group.options"
                  :key="product.value"
                  :value="product.value"
                  :label="product.label">
                  </el-option>
              </el-option-group>
            </el-select>
          </el-form-item>
          <el-form-item label="Channel" prop="channel">
            <el-select
              :value="form.channel"
              @input="updateForm({channel: $event})"
              filterable
              clearable>
              <el-option
                v-for="channel in channels"
                :key="channel.id"
                :value="channel.prefix"
                :label="`${channel.prefix} (${channel.name})`" />
            </el-select>
            <create-channel @create="channelCreated($event)" />
          </el-form-item>
          <el-form-item label="Expiry date" prop="expire">
            <el-date-picker
              :value="form.expire"
              @input="updateForm({expire: $event})"
              type="date"
              placeholder="Pick a day"
              :picker-options="datePickerOptions">
            </el-date-picker>
          </el-form-item>
          <el-form-item label="Amount">
            <el-slider
              :value="form.amount"
              @input="updateForm({amount: $event})"
              :min="1"
              :max="500"
              show-input>
            </el-slider>
          </el-form-item>
          <el-form-item label-width="120px">
            <el-button type="primary" @click="generate">Generate</el-button>
            <el-button @click="reset">Reset</el-button>
          </el-form-item>
        </el-form>
      </div>
      <div class="codes">
        <h5>Voucher Codes
        <el-tooltip :content="copyMessage" :disabled="copyTooltipDisabled" placement="bottom">
          <el-button type="primary" size="mini" @click="copy">Copy to clipboard</el-button>
        </el-tooltip>
        </h5>
        <el-input type="textarea" ref="codes" :value="codes('\n')" :rows="8" readonly></el-input>
      </div>
    </div>
    <voucher-redeem />
  </div>
</template>

<style lang="scss">
  .vouchers {
    margin: 20px;
  }
  .voucher-generator {
    display: flex;
    flex-direction: row;

    & > div {
      flex-grow: 2;
      padding: 20px 20px 0 20px;
      background: #EFF2F7;

      &:last-child {
        flex-grow: 1;
        background: #F9FAFC;
        border-left: 1px dashed #C0CCDA;
        padding-bottom: 20px;
      }
    }
  }

  @media only screen and (max-width : 992px) {
    .voucher-generator {
      flex-direction: column;

      & > div:last-child {
        border-left: none;
        border-top: 1px dashed #C0CCDA;
      }
    }
  }
</style>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'

import CreateChannel from './CreateChannel'
import VoucherRedeem from './VoucherRedeem'

export default {
  components: {
    CreateChannel,
    VoucherRedeem
  },

  data () {
    const isAfter = (rule, value, callback) => {
      moment(value).isAfter(moment())
        ? callback()
        : callback(new Error('Expiry Date has to be a future date'))
    }

    return {
      generating: false,
      copyMessage: '',
      copyTooltipDisabled: true,
      formRules: {
        id: [
          { required: true, message: 'Please select a Product', trigger: 'change' }
        ],
        expire: [
          { type: 'date', required: true, message: 'Please select an Expiry Date', trigger: 'change' },
          { validator: isAfter, trigger: 'change' }
        ]
      },
      datePickerOptions: {
        shortcuts: [{
          text: 'In a Week',
          onClick: picker => picker.$emit('pick', moment().add(1, 'w').toDate())
        }, {
          text: 'In a Month',
          onClick: picker => picker.$emit('pick', moment().add(1, 'M').toDate())
        }, {
          text: 'In a Quarter',
          onClick: picker => picker.$emit('pick', moment().add(3, 'M').toDate())
        }, {
          text: 'In a Year',
          onClick: picker => picker.$emit('pick', moment().add(1, 'y').toDate())
        }]
      }
    }
  },

  methods: {
    ...mapActions('vouchers', [
      'fetchProducts',
      'fetchChannels',
      'updateForm',
      'resetForm',
      'createVouchers'
    ]),
    generate () {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.generating = true
          this.createVouchers().then(() => {
            this.generating = false
          }, () => {
            this.generating = false
          })
        }
      })
    },
    reset () {
      this.resetForm()
      this.$refs['form'].resetFields()
    },
    copy () {
      const textarea = this.$refs.codes.$el.children[0]
      textarea.select()
      textarea.setSelectionRange(0, textarea.value.length)
      const msg = document.execCommand('copy')
        ? 'Copied to your clipboard!'
        : 'Not supported, please select and copy manually.'
      this.notifyCopyStatus(msg)
      window.getSelection().removeAllRanges()
    },
    notifyCopyStatus (msg) {
      this.copyMessage = msg
      this.copyTooltipDisabled = false
      setTimeout(() => {
        this.copyTooltipDisabled = true
      }, 700)
    },
    channelCreated ({ prefix }) {
      this.updateForm({ channel: prefix })
    }
  },

  computed: {
    ...mapState('vouchers', {
      form: state => state.form
    }),
    ...mapGetters('vouchers', {
      productOptions: 'productsAsOptions',
      channels: 'channels',
      codes: 'codesAsString'
    })
  },

  mounted () {
    this.fetchProducts()
    this.fetchChannels()
  }
}
</script>



// WEBPACK FOOTER //
// src/components/vouchers/Vouchers.vue